"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import AngleRightBold from "@/icons/angleRightBold.svg";
import LearnMoreIcon from "@/icons/learnMoree.svg";

import "swiper/css";
import "swiper/css/navigation";
import { getTranslation } from "@/i18n/get-translation";
import Image from "next/image";

interface IMedicalDepartment {
  locale: string;
}

interface IMedicalData {
  id: string;
  name: string;
  desc: string;
  icon: any;
}

const medicalData = [
  {
    name: "generalPractitioner",
    desc: "generalPractitionerDescription",
    icon: "/icons/stethoscope.svg",
  },
  {
    name: "dentist",
    desc: "dentistDescription",
    icon: "/icons/dentist.svg",
  },
  {
    name: "nephrology",
    desc: "nephrologyDescription",
    icon: "/icons/nephrology.svg",
  },
  {
    name: "cardiology",
    desc: "cardiologyDescription",
    icon: "/icons/cardiology.svg",
  },
  {
    name: "pulmonary",
    desc: "pulmonaryDescription",
    icon: "/icons/pulmonary.svg",
  },
  {
    name: "orthopedics",
    desc: "orthopedicsDescription",
    icon: "/icons/orthopedics.svg",
  },
  {
    name: "psychologist",
    desc: "psychologistDescription",
    icon: "/icons/psychologist.svg",
  },
  {
    name: "psychiatrist",
    desc: "psychiatristDescription",
    icon: "/icons/psychiatrist.svg",
  },
  {
    name: "physiotherapist",
    desc: "physiotherapistDescription",
    icon: "/icons/physiotherapist.svg",
  },
];

const navigation = medicalData.map((item, index) => ({
  id: (index + 1).toString(), // Generate the ID dynamically
  ...item,
}));

export default function MedicalDepartment({ locale }: IMedicalDepartment) {
  const t = getTranslation(locale, "home.medicalDepartments");
  return (
    <section className="relative text-center  mt-16 mb-14 max-md:mt-3 max-md:mb-3">
      <div className="text-2xl text-secondary-800 mb-5 max-md:text-lg font-normal">
        {t("medicalDepartmentsTitle")}
      </div>
      <h2 className="text-[3rem] max-md:text-3xl leading-[1.15] font-medium max-w-160 mx-auto text-primary-800">
        {t("ourCommitmentHealthcareExcellence")}
      </h2>
      <div className="mt-16">
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={16}
          slidesPerView={1.2}
          height={500}
          navigation={{
            nextEl: ".next-slide",
            prevEl: ".prev-slide",
          }}
          pagination={true}
          className="relative px-7!"
          breakpoints={{
            1199: {
              slidesPerView: 5.2,
            },
            992: {
              slidesPerView: 3.2,
              pagination: false,
            },
            650: {
              slidesPerView: 2.2,
            },
          }}
        >
          {navigation.map((item: IMedicalData) => (
            <SwiperSlide key={item.id}>
              <article className="bg-primary-500 p-5 rounded-2xl text-left min-h-80 flex flex-col justify-start card-hover">
                <figure className="mb-3">
                  <Image alt="" src={item.icon} className="w-15 h-15" width={60} height={60} />
                </figure>
                <h3 className="text-xl text-primary-700 font-medium mb-[0.813rem]">
                  {t(item.name)}
                </h3>
                <p className="text-primary-800 text-xl font-light pr-4">
                  {t(item.desc)}
                </p>
                <div className="text-right leading-0 self-end mt-auto">
                  <button aria-label={`Learn more about ${t(item.name)}`}>
                    <LearnMoreIcon className="w-10" />
                  </button>
                </div>
              </article>
            </SwiperSlide>
          ))}
          <div className="absolute z-10 right-0 top-0 bg-slider-gradient h-full w-52 max-md:hidden"></div>
          <button aria-label="Previous slide" className="prev-slide absolute top-2/4 -translate-y-2/4 z-20 [&.swiper-button-disabled]:opacity-0 left-25.5 max-md:hidden">
            <div className="relative">
              <div className="bg-primary-500 rounded-full w-14 h-14 drop-shadow-[0px_14px_50px_#BE57DD]"></div>
              <AngleRightBold className="h-5.5 absolute left-2/4 top-2/4 -translate-1/2 rotate-180" />
            </div>
          </button>
          <button aria-label="Next slide" className="next-slide absolute top-2/4 -translate-y-2/4 z-20 [&.swiper-button-disabled]:opacity-0 right-25.5 max-md:hidden">
            <div className="relative">
              <div className="bg-primary-500 rounded-full w-14 h-14 drop-shadow-[0px_14px_50px_#BE57DD]"></div>
              <AngleRightBold className="h-5.5 absolute left-2/4 top-2/4 transform-[translate(-50%,-50%)]" />
            </div>
          </button>
        </Swiper>
      </div>
    </section>
  );
}
